/**
 * serverErrorMessage — the ONE shared extract+decode point for the inline
 * server-error alerts the profile/pay modals render (Phase 6.2 gap-closure,
 * UAT Test 7; PROF-02 / PROF-06).
 *
 * Rails returns its 422 `message` HTML-ENTITY-ENCODED (`can't` → `can&#39;t`).
 * The catch blocks across SecurityTab / DeleteAddressModal / PayModal each bind
 * the raw `response.data.message` via `{{ }}` text interpolation — and Vue
 * interpolation escapes for OUTPUT but does NOT decode entities already present
 * in the string, so `&#39;` renders literally. Every other server-text sink in
 * the app (QuotesView, InvoicesView, …) already runs its text through
 * `decodeHtml`; the error-alert path was the one place that did not.
 *
 * This helper folds the triplicated `response.data.message || err.message ||
 * fallback` chain those catch blocks duplicate into a single function AND runs
 * the result through `decodeHtml`, so the fix lands once and stays consistent.
 *
 * SECURITY (T-06.2-G2-01): the return is a decoded PLAIN string (decodeHtml's
 * `textarea.value` extraction). It is bound via `{{ }}` text interpolation only
 * — NEVER `v-html` — so decoded markup cannot re-enter the DOM. The fallback is
 * developer-supplied plain text; passing it through `decodeHtml` too keeps a
 * single code path and leaves plain text unaffected.
 */
import { decodeHtml } from './decodeHtml';

/** Loose shape covering an axios error or a raw thrown value. */
interface ServerErrorLike {
  message?: unknown;
  response?: {
    data?: {
      message?: unknown;
    };
  };
}

/**
 * Extract the customer-facing message from a thrown error and decode any HTML
 * entities. Resolution order: `response.data.message` → `err.message` →
 * `fallback`. The first non-empty string wins; the chosen value (including the
 * fallback) is returned through `decodeHtml`.
 */
export function extractServerErrorMessage(
  err: unknown,
  fallback: string,
): string {
  const e: ServerErrorLike =
    err && typeof err === 'object' ? (err as ServerErrorLike) : {};
  const serverMessage = e.response?.data?.message;
  const transportMessage = e.message;
  const raw =
    (typeof serverMessage === 'string' && serverMessage) ||
    (typeof transportMessage === 'string' && transportMessage) ||
    fallback;
  return decodeHtml(raw);
}
